import { Button, CircularProgress, Grid, Typography } from '@material-ui/core'
import { goBack } from 'connected-react-router'
import moment, { Moment } from 'moment'
import { FC, Fragment, useEffect, useState } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { Dispatch } from 'redux'
import {
  Field,
  reduxForm,
  arrayRemoveAll,
  formValueSelector,
  change,
  reset,
  submit
} from 'redux-form'
import { navigate } from '../../../../actions/routes'
import {
  createShowings,
  addShowingToFieldArray
} from '../../../../actions/showings'
import { Navigate } from '../../../../actions/types'
import SubmitFormButton from '../../../../components/Buttons/SubmitFormButton'
import ErrorMessage from '../../../../components/Error/ErrorMessage'
import {
  ReduxDatePicker,
  ReduxTimePicker
} from '../../../../components/FormInput/ReduxWrappers'
import { selectFormValues } from '../../../../selectors/form'
import {
  selectContentIdFromPathname,
  selectUserIdFromPathname
} from '../../../../selectors/router'
import {
  selectShowingsInterfaceByApartmentId,
  selectShowingsLoading
} from '../../../../selectors/showings'
import { selectIsMobile, selectWindowWidth } from '../../../../selectors/users'
import i18n from '../../../../translations/i18n'
import { MuiClasses } from '../../../../types'
import { ReduxForm } from '../../../../types/forms'
import {
  AddShowingsFormData,
  FormShowing,
  Showing
} from '../../../../types/showings'
import {
  EmptyShowingsInterface,
  StateInterface,
  CreateShowingsFormState
} from '../../../../types/states'
import { bindSubmitActionToPromise } from '../../../../utils/forms'
import { getFutureShowings } from '../../../../utils/showings/filterAndSearch'
import {
  required,
  lessThanEndDate,
  validateTime,
  validateDayTime
} from '../../../../utils/validation/forms'
import Suggest from '../../../Apartment/Stepper/Steps/Suggest'
import CustomCalendar from './Inputs/CustomCalendar'
import TimePickTable from './Inputs/TimePickTable'

interface AddShowingsFormProps extends WithTranslation, ReduxForm {
  addShowingToFieldArray: (formData: AddShowingsFormData) => void
  formDate: string | undefined
  isPreview?: boolean
  onFormSubmit?: () => void
  isDialog?: boolean
}

interface StateProps {
  userId?: string
  apartmentId?: string
  futureShowings: Showing[]
  showings?: FormShowing
  classes: MuiClasses
  formDate: Moment
  showingsLoading: boolean
  isMobile: boolean
  windowWidth: string
}

interface DispatchProps {
  arrayRemoveAllAction: (form: string, field: string) => void
  navigate: Navigate
  resetForm: () => void
  changeFieldValue: (fieldName: string, value: any) => void
  goBack: () => void
  createShowingsAction: (formData: CreateShowingsFormState) => void
}

type ComponentProps = AddShowingsFormProps & StateProps & DispatchProps

const renderTimePickTable = (props: any) => <TimePickTable {...props} />

const AddShowingsForm: FC<ComponentProps> = ({
  handleSubmit,
  submitting,
  error,
  onFormSubmit,
  addShowingToFieldArray,
  t,
  resetForm,
  showings,
  changeFieldValue,
  isDialog,
  navigate,
  formDate,
  goBack,
  userId,
  apartmentId,
  showingsLoading,
  classes,
  isMobile,
  windowWidth,
  createShowingsAction
}) => {
  const [suggestionRequired, setSuggestionRequired] = useState<boolean>(true)
  const [errorEmptyDates, setErrorEmptyDates] = useState<boolean>(false)
  const [loadingNextButton, setLoadingNextButton] = useState<boolean>(false)

  useEffect(() => {
    if (Array.isArray(showings) && showings.length > 0) {
      setErrorEmptyDates(false)
      if (showings.length === 1 && suggestionRequired) {
        toast.info(i18n.t('showings.createShowings.messages.suggestions'), {
          autoClose: 10000
        })
        setSuggestionRequired(false)
      }
    } else {
      if (!suggestionRequired) {
        setSuggestionRequired(true)
      }
    }
  }, [showings])

  const handleOnClick = () => {
    if (!Array.isArray(showings) || showings.length < 1) {
      setErrorEmptyDates(true)
    } else {
      setLoadingNextButton(true)
      createShowingsAction({
        showings: showings
      })
    }
  }

  // useEffect(() => {
  //   if (Array.isArray(showings) && showings.length > 0) {
  //     resetForm()
  //     changeFieldValue('date', moment(showings[showings.length - 1].startDate))
  //   }
  // }, [changeFieldValue, resetForm, showings])
  return (
    <Fragment>
      {!isDialog && (
        <Fragment>
          <form onSubmit={handleSubmit(addShowingToFieldArray)}>
            <Grid container>
              <Grid item xs={12} md={7}>
                <Grid container style={{ maxWidth: '520px', margin: 'auto' }}>
                  {isMobile && (
                    <Suggest
                      contentMaxWidth='450px'
                      containerMaxWidth='450px'
                      content={t('showings.reserve.hints.landLordSuggestTime')}
                    />
                  )}
                  <Grid item xs={12}>
                    <Typography
                      variant='h5'
                      align='center'
                      className={classes.heading}
                    >
                      {t('showings.createShowings.form.suggestDate')}
                    </Typography>
                    <Field
                      fullWidth
                      name='date'
                      label={t('showings.createShowings.form.date')}
                      validate={[required]}
                      component={CustomCalendar}
                      disablePast={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {formDate &&
                      (windowWidth === 'sm' || windowWidth === 'xs') && (
                        <>
                          <Typography
                            variant='h5'
                            align='center'
                            className={classes.heading}
                          >
                            {t('showings.createShowings.form.suggestTime')}{' '}
                            {moment(formDate).format('DD.MM')}
                          </Typography>
                          <Field
                            name='startDate'
                            label={t('showings.createShowings.form.startTime')}
                            validate={[required, validateTime]}
                            component={renderTimePickTable}
                            submittingLoad={submitting}
                            handleSubmit={handleSubmit}
                            addShowingToFieldArray={addShowingToFieldArray}
                            placeholder='Placeholder'
                            error={error}
                            activeDates={
                              Array.isArray(showings) &&
                              showings.length > 0 &&
                              showings.map((showing) => showing.startDate)
                            }
                          />
                        </>
                      )}
                  </Grid>
                  <Grid item xs={12}>
                    {errorEmptyDates && (
                      <Typography
                        align='center'
                        style={{ padding: '15px 0 0 0' }}
                      >
                        <ErrorMessage
                          error={t('error.showings.dateIsRequired')}
                        />
                      </Typography>
                    )}
                  </Grid>
                  <Grid container style={{ marginTop: '35px' }}>
                    <Grid item xs={4}>
                      <Button fullWidth onClick={() => goBack()}>
                        {t('navigation.sub.back')}
                      </Button>
                    </Grid>
                    <Grid item xs={8}>
                      <Button
                        className={classes.buttonNext}
                        variant='contained'
                        color='primary'
                        fullWidth
                        onClick={handleOnClick}
                        disabled={loadingNextButton}
                      >
                        {loadingNextButton ? (
                          <CircularProgress
                            size={18}
                            style={{
                              color: '#fff',
                              marginLeft: '5px'
                            }}
                          />
                        ) : (
                          <>{t('showings.inviteApplicant')}</>
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid md={5} xs={12}>
                <>
                  {!isMobile && (
                    <Suggest
                      contentMaxWidth='450px'
                      containerMaxWidth='450px'
                      content={t('showings.reserve.hints.landLordSuggestTime')}
                    />
                  )}
                  {formDate &&
                    (windowWidth === 'md' ||
                      windowWidth === 'lg' ||
                      windowWidth === 'xl') && (
                      <Grid
                        item
                        xs={12}
                        style={{ maxWidth: '450px', margin: 'auto' }}
                      >
                        <Typography
                          variant='h5'
                          align='center'
                          style={{
                            margin: '1rem 0rem 0.5rem 0rem',
                            paddingTop: '20px'
                          }}
                        >
                          {t('showings.createShowings.form.suggestTime')}{' '}
                          {moment(formDate).format('DD.MM')}
                        </Typography>
                        <Field
                          name='startDate'
                          label={t('showings.createShowings.form.startTime')}
                          validate={[required, validateTime]}
                          component={renderTimePickTable}
                          handleSubmit={handleSubmit}
                          addShowingToFieldArray={addShowingToFieldArray}
                          placeholder='Placeholder'
                          error={error}
                          activeDates={
                            Array.isArray(showings) &&
                            showings.length > 0 &&
                            showings.map((showing) => showing.startDate)
                          }
                        />
                      </Grid>
                    )}
                </>
              </Grid>
            </Grid>
          </form>
        </Fragment>
      )}
      {isDialog && (
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Grid container spacing={1}>
            <Typography
              variant='body2'
              color='textSecondary'
              style={{ width: '100%', paddingLeft: '5px' }}
            >
              {t('application.headings.date')}
            </Typography>
            <Grid item xs={6}>
              <Field
                fullWidth
                name='date'
                label={t('showings.createShowings.form.date')}
                validate={[required]}
                component={ReduxDatePicker}
                inputVariant='outlined'
                disablePast={true}
              />
            </Grid>
            <Grid item xs={6}>
              <Field
                fullWidth
                name='startDate'
                label={t('showings.createShowings.form.startTime')}
                validate={[
                  required,
                  validateTime,
                  lessThanEndDate,
                  validateDayTime
                ]}
                component={ReduxTimePicker}
                minutesStep={5}
              />
            </Grid>
            <ErrorMessage error={error} />
            <Grid
              container
              alignItems='flex-end'
              justify='flex-end'
              spacing={1}
            >
              <Grid item xs={12} style={{ marginTop: '8px' }}>
                <SubmitFormButton
                  fullWidth
                  textColor='white'
                  borderRadius='4px'
                  variant='contained'
                  submitting={submitting}
                  label='application.inviteApplicant'
                />
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    </Fragment>
  )
}
const selector = formValueSelector('CreateShowingsForm')
const mapStateToProps = (state: StateInterface) => {
  const apartmentId = selectContentIdFromPathname(state)
  const showingsInterface = apartmentId
    ? selectShowingsInterfaceByApartmentId(apartmentId)(state)
    : EmptyShowingsInterface

  return {
    userId: selectUserIdFromPathname(state),
    apartmentId,
    isMobile: selectIsMobile(state),
    windowWidth: selectWindowWidth(state),
    futureShowings: getFutureShowings(showingsInterface.showings, 'reserve'),
    showings: selector(state, 'showings'),
    formDate: formValueSelector('AddShowingsForm')(state, 'date'),
    showingsLoading: selectShowingsLoading(state),
    createShowingsForm: selectFormValues('CreateShowingsForm')(state)
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  navigate: (path: string) => dispatch(navigate(path)),
  addShowingToFieldArray: bindSubmitActionToPromise(
    dispatch,
    addShowingToFieldArray
  ),
  arrayRemoveAllAction: (form: string, field: string) =>
    dispatch(arrayRemoveAll(form, field)),
  changeFieldValue: (fieldName: string, value: any) =>
    dispatch(change('AddShowingsForm', fieldName, value)),
  resetForm: () => dispatch(reset('AddShowingsForm')),
  submit: () => dispatch(submit('AddShowingsForm')),
  goBack: () => dispatch(goBack()),
  createShowingsAction: bindSubmitActionToPromise(dispatch, createShowings)
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: 'AddShowingsForm'
  })(withTranslation()(AddShowingsForm) as any) as any
) as any
